import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IProviderOrganization } from '@common/interfaces';
import type { IAutoSubsequentServiceValidated } from '@common/validation/serviceConsumer';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, ServiceCode, ServiceType } from '@common/enums/';
import { serviceConsumerPersonalSettingsEditSchema } from '@common/validation/serviceConsumer';
import { AbortableFetch } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { IServiceConsumerWithAutoSubsequentService } from '../../../interfaces/serviceConsumer';
import type { IServiceOrganizationSettings } from '../../../interfaces/serviceOrganizationSettings';
import type { ISubsequentService } from '../../../interfaces/subsequentService';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { TurnRightOffLink } from '../carePathSetting/TurnRightOffLink';
import { CloseServiceAndSubsequentServiceSettings } from './CloseServiceAndSubsequentServiceSettings';
import { DateSettings } from './DateSettings';
import { FollowUpMeasurementGgz } from './FollowUpMeasurementGgz';
import { RightRequestSettings } from './RightRequestSettings';
import { SelectGraderSettings } from './SelectGraderSettings';
import { SoftwareSettings } from './SoftwareSettings';
import { StartRequestSettings } from './StartRequestSettings';

interface IPersonalSettingsCarePathServiceConsumerSettingProps {
    employmentId: string;
    organizationId: string;
    reloadServiceConsumerRight: () => Promise<void>;
    serviceConsumerRight: IServiceConsumerWithAutoSubsequentService;
}

export interface IPersonSettingCarePathServiceConsumerSettings
    extends Omit<IServiceConsumerWithAutoSubsequentService, 'autoSubsequentService'> {
    autoSubsequentService: IAutoSubsequentServiceEditVersion;
    hasAutoSubsequentService: boolean;
}

export interface IAutoSubsequentServiceEditVersion {
    isDeviation?: boolean;
    providerOrganization?: IProviderOrganization;
    providerOrganizationId?: string;
    providerPractitioner?: IPractitioner;
    providerPractitionerId?: string;
    subsequentService?: {
        succeedingService?: {
            id?: string;
            name?: string;
        };
    };
    subsequentServiceId?: string;
}

export type PersonalSettingsCarePathServiceConsumerFormMode = 'read' | 'edit';

export interface IPersonalSettingsCarePathServiceConsumerSettingFormSectionProps
    extends IPersonSettingCarePathServiceConsumerSettings {
    errors: ValidationErrors;
    handleChange: (
        key: keyof IPersonSettingCarePathServiceConsumerSettings,
        value: IPersonSettingCarePathServiceConsumerSettings[keyof IPersonSettingCarePathServiceConsumerSettings]
    ) => void;
    mode: PersonalSettingsCarePathServiceConsumerFormMode;
    validateOne: (key: keyof IPersonSettingCarePathServiceConsumerSettings) => void;
}

export function PersonalSettingsCarePathServiceConsumerSetting(
    props: IPersonalSettingsCarePathServiceConsumerSettingProps
) {
    const abortableFetch = new AbortableFetch();
    const [isSaving, setIsSaving] = useState(false);
    const [mode, setMode] = useState<PersonalSettingsCarePathServiceConsumerFormMode>('read');
    const [
        personalSettingsCarePathServiceConsumerSettingEditVersion,
        setPersonalSettingsCarePathServiceConsumerSettingEditVersion,
    ] = useState<IPersonSettingCarePathServiceConsumerSettings | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [possibleSubsequentServices, setPossibleSubsequentServices] = useState<ISubsequentService[]>([]);

    if (
        !props.serviceConsumerRight.service.serviceType ||
        ![
            ServiceType.examination,
            ServiceType.consultation,
            ServiceType.referral,
            ServiceType.referralSmall,
            ServiceType.counseling,
            ServiceType.other,
            ServiceType.contact,
        ].includes(props.serviceConsumerRight.service.serviceType)
    ) {
        return null;
    }

    useEffect(() => {
        loadSubsequentServices();
    }, [props.serviceConsumerRight.service.id]);

    useEffect(() => {
        if (mode === 'edit') {
            setPersonalSettingsCarePathServiceConsumerSettingEditVersion(createServiceConsumerEditVersion());
        } else {
            setPersonalSettingsCarePathServiceConsumerSettingEditVersion(null);
            setErrors(emptyValidationErrors);
        }
    }, [mode, props.serviceConsumerRight]);

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'serviceConsumerSetting',
                item: personalSettingsCarePathServiceConsumerSettingEditVersion || {
                    ...props.serviceConsumerRight,
                    hasAutoSubsequentService: !!props.serviceConsumerRight.autoSubsequentService,
                },
                errors: emptyValidationErrors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={mode} />
                <Card.Header
                    action={
                        showEditIcon() ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setMode('edit')}
                            />
                        ) : undefined
                    }
                    title={`${translateKey('addCarePathSectionTitles.serviceConsumerSettings')} ${
                        props.serviceConsumerRight.service.name
                    }`}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        <RightRequestSettings
                            isAutoCreated={props.serviceConsumerRight.isAutoCreated}
                            isEditMode={mode === 'edit'}
                            onChange={handleChange}
                            setToRequestedRightDateTime={props.serviceConsumerRight.setToRequestedRightDateTime}
                        />
                        <DateSettings serviceRight={props.serviceConsumerRight} />
                        <StartRequestSettings {...getPropsForFormSection()} />
                        <CloseServiceAndSubsequentServiceSettings
                            {...getPropsForFormSection()}
                            employmentId={props.employmentId}
                            possibleSubsequentServices={possibleSubsequentServices}
                        />
                        <SelectGraderSettings {...getPropsForFormSection()} />
                        <SoftwareSettings
                            {...getPropsForFormSection()}
                            serviceRightType={'ServiceConsumer'}
                        />
                        <FollowUpMeasurementGgz {...getPropsForFormSection()} />

                        {mode === 'edit' && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => setMode('read')}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
                {!props.serviceConsumerRight.endDate &&
                    hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) && (
                        <Card.Footer>
                            <TurnRightOffLink
                                organizationId={props.organizationId}
                                reloadServiceRights={async () => {
                                    await props.reloadServiceConsumerRight();
                                    setMode('read');
                                }}
                                serviceRight={props.serviceConsumerRight}
                                serviceRightType={'ServiceConsumer'}
                            />
                        </Card.Footer>
                    )}
            </Card>
        </FormContextProvider>
    );

    async function loadSubsequentServices() {
        const url = `/api/subsequentServices?serviceId=${props.serviceConsumerRight.service.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPossibleSubsequentServices(result.json.items);
    }

    function createServiceConsumerEditVersion() {
        return {
            ...props.serviceConsumerRight,
            hasAutoSubsequentService: !!props.serviceConsumerRight.autoSubsequentService,
            autoSubsequentService: !props.serviceConsumerRight.autoSubsequentService
                ? {
                      subsequentServiceId: undefined,
                      isDeviation: false,
                      providerOrganizationId: undefined,
                      providerPractitionerId: undefined,
                      providerOrganization: undefined,
                      providerPractitioner: undefined,
                      subsequentService: {
                          succeedingService: {
                              id: undefined,
                              name: undefined,
                          },
                      },
                  }
                : {
                      ...props.serviceConsumerRight.autoSubsequentService,
                      isDeviation: !!props.serviceConsumerRight.autoSubsequentService.isDeviation,
                  },
        };
    }

    function getPropsForFormSection(): IPersonalSettingsCarePathServiceConsumerSettingFormSectionProps {
        const propsForFormSection = {
            mode,
            errors,
            handleChange: handleChange,
            validateOne: validateOne,
        };
        if (mode === 'edit' && personalSettingsCarePathServiceConsumerSettingEditVersion) {
            return { ...propsForFormSection, ...personalSettingsCarePathServiceConsumerSettingEditVersion };
        } else {
            return { ...propsForFormSection, ...createServiceConsumerEditVersion() };
        }
    }

    function showEditIcon() {
        if (!hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit)) {
            return false;
        }

        return (
            (mode === 'read' &&
                props.serviceConsumerRight.service.serviceType &&
                ![ServiceType.referralSmall, ServiceType.counseling, ServiceType.contact].includes(
                    props.serviceConsumerRight.service.serviceType
                )) ||
            (mode === 'read' &&
                props.serviceConsumerRight.service.serviceType === ServiceType.referralSmall &&
                possibleSubsequentServices.length > 0) ||
            (mode === 'read' &&
                props.serviceConsumerRight.service.serviceType === ServiceType.counseling &&
                possibleSubsequentServices.length > 0)
        );
    }

    function handleChange<K extends keyof IPersonSettingCarePathServiceConsumerSettings>(
        name: K,
        value: IPersonSettingCarePathServiceConsumerSettings[K]
    ) {
        setPersonalSettingsCarePathServiceConsumerSettingEditVersion({
            ...personalSettingsCarePathServiceConsumerSettingEditVersion,
            [name]: value,
        } as IPersonSettingCarePathServiceConsumerSettings);
    }

    function validateOne(name: keyof IPersonSettingCarePathServiceConsumerSettings) {
        setErrors(
            updateValidationErrorsForOne(
                serviceConsumerPersonalSettingsEditSchema,
                personalSettingsCarePathServiceConsumerSettingEditVersion,
                errors,
                name
            )
        );
    }

    async function handleSubmit() {
        if (personalSettingsCarePathServiceConsumerSettingEditVersion) {
            setIsSaving(true);
            const itemToSave = createPersonalSettingsCarePathServiceConsumerSettingToSave();
            const { errors: validationErrors, item } = validate(serviceConsumerPersonalSettingsEditSchema, itemToSave);
            let hasAutoSubsequentServiceErrors = false;

            if (item?.autoSubsequentService) {
                hasAutoSubsequentServiceErrors = await validateAutoSubsequentService(item.autoSubsequentService);
            }

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else if (hasAutoSubsequentServiceErrors) {
                setErrors(errors.set('autoSubsequentService.providerPractitionerId', 'any_required'));
            } else {
                const url = `/api/serviceConsumers/${props.serviceConsumerRight.id}`;
                await abortableFetch.fetchJson(url, 'POST', item);
                await props.reloadServiceConsumerRight();
                setErrors(emptyValidationErrors);
                setMode('read');
            }
            setIsSaving(false);
        }
    }

    function createPersonalSettingsCarePathServiceConsumerSettingToSave() {
        if (personalSettingsCarePathServiceConsumerSettingEditVersion) {
            const usingSpirometer =
                personalSettingsCarePathServiceConsumerSettingEditVersion.usingSpirometer &&
                props.serviceConsumerRight.service.code === ServiceCode.TPC;
            const spirometerSoftwareName = usingSpirometer
                ? personalSettingsCarePathServiceConsumerSettingEditVersion.spirometerSoftwareName
                : null;
            const usingRestEcgMeter =
                personalSettingsCarePathServiceConsumerSettingEditVersion.usingRestEcgMeter &&
                props.serviceConsumerRight.service.code &&
                [ServiceCode.TCCi, ServiceCode.TCCh, ServiceCode.TCCr].includes(
                    props.serviceConsumerRight.service.code
                );
            const followUpMeasurementGgz =
                personalSettingsCarePathServiceConsumerSettingEditVersion.followUpMeasurementGgz &&
                props.serviceConsumerRight.service.code &&
                [ServiceCode.GGZOembl, ServiceCode.GGZOint].includes(props.serviceConsumerRight.service.code);
            const weeksForFollowUpMeasurementGgz = followUpMeasurementGgz
                ? personalSettingsCarePathServiceConsumerSettingEditVersion.weeksForFollowUpMeasurementGgz
                : null;

            const settingToSave = {
                startDate: personalSettingsCarePathServiceConsumerSettingEditVersion.startDate,
                isAutoCreated: personalSettingsCarePathServiceConsumerSettingEditVersion.isAutoCreated,
                isRequestCreator: personalSettingsCarePathServiceConsumerSettingEditVersion.isRequestCreator,
                isAutoCloseRequest: personalSettingsCarePathServiceConsumerSettingEditVersion.isAutoCloseRequest,
                autoSubsequentService:
                    personalSettingsCarePathServiceConsumerSettingEditVersion.hasAutoSubsequentService
                        ? {
                              subsequentServiceId:
                                  personalSettingsCarePathServiceConsumerSettingEditVersion.autoSubsequentService
                                      ?.subsequentServiceId,
                              isDeviation:
                                  !!personalSettingsCarePathServiceConsumerSettingEditVersion.autoSubsequentService
                                      ?.isDeviation,
                              providerOrganizationId:
                                  personalSettingsCarePathServiceConsumerSettingEditVersion.autoSubsequentService
                                      ?.providerOrganizationId,
                              providerPractitionerId:
                                  personalSettingsCarePathServiceConsumerSettingEditVersion.autoSubsequentService
                                      ?.providerPractitionerId || null,
                          }
                        : null,
                isGraderSelector: personalSettingsCarePathServiceConsumerSettingEditVersion.isGraderSelector,
                usingSpirometer,
                spirometerSoftwareName,
                usingRestEcgMeter,
                followUpMeasurementGgz,
                weeksForFollowUpMeasurementGgz,
            };

            return settingToSave;
        }

        return {};
    }

    async function validateAutoSubsequentService(autoSubsequentService: IAutoSubsequentServiceValidated) {
        const selectedSucceedingServiceId = possibleSubsequentServices.find(
            (subsequentService) => subsequentService.id === autoSubsequentService.subsequentServiceId
        );
        const result = await abortableFetch.fetchJson(
            `/api/serviceOrganizationSettings?serviceId=${selectedSucceedingServiceId?.succeedingServiceId}&organizationId=${autoSubsequentService.providerOrganizationId}&isActive=true`,
            'GET'
        );
        const serviceOrganizations = result.json.items as IServiceOrganizationSettings[];
        let isSelectedSubsequentServiceOrganizationRequestRecipient = false;

        if (serviceOrganizations.length > 0) {
            isSelectedSubsequentServiceOrganizationRequestRecipient = serviceOrganizations[0].isRequestRecipient;
        }

        if (isSelectedSubsequentServiceOrganizationRequestRecipient || autoSubsequentService.providerPractitionerId) {
            return false;
        }

        return true;
    }
}
